import { Link } from "react-router-dom";
import { Bot, ArrowLeft, ShieldCheck } from "lucide-react";

const SECTIONS = [
  {
    title: "Information We Collect",
    body: "When you contact us on WhatsApp, we collect your name, phone number, and the content of the messages you send us. We may also store basic details you share during a conversation (such as your city or an enquiry reference) so we can serve your request.",
  },
  {
    title: "How We Use Your Information",
    body: "We use this information solely to respond to your customer enquiries, route your request to the right team, and send you relevant service updates and reminders over WhatsApp. We do not use your data for unrelated advertising.",
  },
  {
    title: "Data Sharing",
    body: "We do not sell your personal data to third parties. Your information is shared only with the messaging providers required to deliver WhatsApp messages on our behalf (for example, WhatsApp/Meta and our messaging partner), and only to the extent needed to communicate with you.",
  },
  {
    title: "Data Retention",
    body: "We keep your conversation history for as long as needed to support you and to maintain our service records. You may ask us to delete your data at any time using the contact details below.",
  },
  {
    title: "Your Choices",
    body: "You can opt out of service updates at any time by replying STOP on WhatsApp or by emailing us. You may also request a copy of, or the deletion of, the personal data we hold about you.",
  },
  {
    title: "Contact Us",
    body: "For any privacy questions or requests, please email us at dinesh@dvepl.com and we will be happy to help.",
  },
];

export default function PrivacyPolicy() {
  return (
    <div data-testid="privacy-policy-page" className="min-h-screen bg-[#0B0F17] text-slate-200 relative overflow-hidden">
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-emerald-500/10 rounded-full blur-[120px] pointer-events-none" />
      <div className="absolute top-1/2 -left-40 w-[400px] h-[400px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none" />

      {/* nav */}
      <header className="relative z-10 max-w-4xl mx-auto flex items-center justify-between px-6 h-20">
        <Link to="/" data-testid="privacy-home-link" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-600 flex items-center justify-center shadow-lg shadow-emerald-500/20">
            <Bot className="w-5 h-5 text-[#0B0F17]" />
          </div>
          <span className="font-display font-extrabold text-xl tracking-tight text-white">DVEPL</span>
        </Link>
        <Link to="/" data-testid="privacy-back-link"
          className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-emerald-400 transition-colors">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>
      </header>

      {/* content */}
      <main className="relative z-10 max-w-3xl mx-auto px-6 pb-28 pt-6">
        <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/20 bg-emerald-500/10 px-3 py-1 text-xs font-medium text-emerald-300">
          <ShieldCheck className="w-3.5 h-3.5" /> Privacy Policy
        </div>
        <h1 className="mt-5 font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
          Privacy Policy
        </h1>
        <p className="mt-4 text-base text-slate-400 leading-relaxed">
          This Privacy Policy explains how <span className="text-slate-200 font-medium">DV Electromatic Pvt Ltd (DVEPL)</span> collects,
          uses, and protects your personal information when you interact with us over WhatsApp.
        </p>

        <div className="mt-10 space-y-4">
          {SECTIONS.map((s, i) => (
            <section key={s.title}
              className="glass rounded-2xl p-6 animate-fade-up"
              style={{ animationDelay: `${i * 0.05}s` }}>
              <h2 className="font-display font-semibold text-lg text-white">{s.title}</h2>
              <p className="mt-2 text-sm text-slate-400 leading-relaxed">
                {s.body.split("dinesh@dvepl.com").map((part, idx, arr) => (
                  <span key={idx}>
                    {part}
                    {idx < arr.length - 1 && (
                      <a href="mailto:dinesh@dvepl.com"
                        data-testid="privacy-contact-email"
                        className="text-emerald-400 hover:text-emerald-300 underline underline-offset-2">
                        dinesh@dvepl.com
                      </a>
                    )}
                  </span>
                ))}
              </p>
            </section>
          ))}
        </div>

        <p className="mt-10 text-xs text-slate-500">
          © {new Date().getFullYear()} DV Electromatic Pvt Ltd (DVEPL). All rights reserved.
        </p>
      </main>
    </div>
  );
}
